import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Building07Icon } from '@/assets/icons/Building07Icon';
import { ChevronDownIcon } from '@/assets/icons/ChevronDownIcon';
import { ChevronUpIcon } from '@/assets/icons/ChevronUpIcon';
import { HelpCircleIcon } from '@/assets/icons/HelpCircleIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { PMPRoutes } from '@/config/routes';

interface NoPropertyLocationProps {
  title?: string;
  description?: string;
  buttonText?: string;
  onCreateClick?: () => void;
}

const NoPropertyLocation: React.FC<NoPropertyLocationProps> = ({
  title = 'Welcome to Terr.ai! 🎉',
  description = "Let's get you started with your first property. It only takes 2 minutes!",
  buttonText = 'Add Your First Property',
  onCreateClick,
}) => {
  const history = useHistory();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const handleCreateClick = () => {
    if (onCreateClick) {
      onCreateClick();
    } else {
      history.push(PMPRoutes.propertyLocationCreate);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        width: '100%',
        p: 3,
      }}
    >
      <Paper
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%',
          textAlign: 'center',
          backgroundColor: 'background.paper',
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Progress indicator */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
          <LinearProgress
            variant="determinate"
            value={0}
            sx={{
              height: 6,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'primary.main',
              },
            }}
          />
        </Box>

        <Stack spacing={3} alignItems="center" sx={{ mt: 4 }}>
          {/* Welcome badge */}
          <Chip
            label="NEW USER"
            size="small"
            sx={{
              backgroundColor: 'success.lighter',
              color: 'success.dark',
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />

          {/* Icon with animation */}
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              backgroundColor: 'primary.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: '0 0 0 0 rgba(157, 21, 0, 0.4)',
                },
                '70%': {
                  boxShadow: '0 0 0 10px rgba(157, 21, 0, 0)',
                },
                '100%': {
                  boxShadow: '0 0 0 0 rgba(157, 21, 0, 0)',
                },
              },
            }}
          >
            <Building07Icon sx={{ fontSize: 50, color: 'primary.main' }} />
          </Box>

          {/* Title */}
          <Typography variant="h4" fontWeight={700} sx={{ color: 'text.primary' }}>
            {title}
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 450, lineHeight: 1.6 }}
          >
            {description}
          </Typography>

          {/* What you'll unlock - Interactive sections */}
          <Stack spacing={2} sx={{ width: '100%', maxWidth: 500 }}>
            <Typography variant="h6" fontWeight={600} sx={{ textAlign: 'center', mb: 1 }}>
              What You'll Unlock
            </Typography>

            {/* Contact Management */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: 'grey.50',
                border: theme => `1px solid ${theme.palette.grey[200]}`,
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  backgroundColor: 'grey.100',
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
              onClick={() => toggleSection('contacts')}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ fontSize: '1.5rem' }}>👥</Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Smart Contact Management
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Organize tenants, vendors, and team members
                    </Typography>
                  </Box>
                </Stack>
                <IconButton size="small">
                  {expandedSection === 'contacts' ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </IconButton>
              </Stack>
              <Collapse in={expandedSection === 'contacts'}>
                <Box sx={{ mt: 2, pl: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Keep all your property contacts in one place. Easily communicate with tenants,
                    track vendor information, and collaborate with your team.
                  </Typography>
                </Box>
              </Collapse>
            </Paper>

            {/* Document Tracking */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: 'grey.50',
                border: theme => `1px solid ${theme.palette.grey[200]}`,
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  backgroundColor: 'grey.100',
                  transform: 'translateY(-2px)',
                  boxShadow: 1,
                },
              }}
              onClick={() => toggleSection('documents')}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ fontSize: '1.5rem' }}>📄</Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Document & Communication Hub
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, textAlign: 'left', display: 'block' }}
                    >
                      Never lose important files again
                    </Typography>
                  </Box>
                </Stack>
                <IconButton size="small">
                  {expandedSection === 'documents' ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </IconButton>
              </Stack>
              <Collapse in={expandedSection === 'documents'}>
                <Box sx={{ mt: 2, pl: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Store and organize important documents such as leases, contracts, invoices,
                    appliance manuals, warranties, HOA documents, insurance policies, and
                    maintenance records. Track all communications with AI-powered search to find
                    anything instantly.
                  </Typography>
                </Box>
              </Collapse>
            </Paper>

            {/* Email Management */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: 'grey.50',
                border: theme => `1px solid ${theme.palette.grey[200]}`,
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  backgroundColor: 'grey.100',
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
              onClick={() => toggleSection('email')}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ fontSize: '1.5rem' }}>📧</Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Unique Property Emails
                      <Tooltip title="Each property gets its own email address for better organization">
                        <HelpCircleIcon
                          sx={{
                            ml: 0.5,
                            fontSize: 16,
                            color: 'text.secondary',
                            visibility: 'hidden',
                          }}
                        />
                      </Tooltip>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Automatic email organization by property
                    </Typography>
                  </Box>
                </Stack>
                <IconButton size="small">
                  {expandedSection === 'email' ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </IconButton>
              </Stack>
              <Collapse in={expandedSection === 'email'}>
                <Box sx={{ mt: 2, pl: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Each property gets a unique email like "123main@mail.terr.ai". All
                    property-related emails are automatically organized and searchable. Best of all?
                    Your email attachements are automatically saved to your property documents and
                    becomes searchable instantly.
                  </Typography>
                </Box>
              </Collapse>
            </Paper>

            {/* Save Together */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: 'primary.lighter',
                border: theme => `1px solid ${theme.palette.primary.light}`,
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
              onClick={() => toggleSection('save')}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ fontSize: '1.5rem' }}>💰</Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                      Save Together Groups
                    </Typography>
                    <Typography
                      variant="caption"
                      color="primary.dark"
                      sx={{ mt: 0.5, textAlign: 'left', display: 'block' }}
                    >
                      Join forces to negotiate better deals
                    </Typography>
                  </Box>
                </Stack>
                <IconButton size="small">
                  {expandedSection === 'save' ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </IconButton>
              </Stack>
              <Collapse in={expandedSection === 'save'}>
                <Box sx={{ mt: 2, pl: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Connect with other community members to negotiate group discounts on insurance,
                    maintenance, and supplies. Save an average of 15-30%!
                  </Typography>
                </Box>
              </Collapse>
            </Paper>
          </Stack>

          {/* CTA Section */}
          <Stack spacing={2} alignItems="center" sx={{ mt: 3 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<PlusIcon />}
              onClick={handleCreateClick}
              sx={{
                px: 5,
                py: 2,
                textTransform: 'none',
                fontSize: '18px',
                fontWeight: 700,
                borderRadius: 2,
                boxShadow: theme => theme.shadows[4],
                background: 'linear-gradient(45deg, #9D1500 30%, #c72b2b 90%)',
                '&:hover': {
                  boxShadow: theme => theme.shadows[8],
                  transform: 'translateY(-2px)',
                  background: 'linear-gradient(45deg, #8D1300 30%, #b72525 90%)',
                },
                transition: 'all 0.3s',
              }}
            >
              {buttonText}
            </Button>

            <Typography variant="caption" color="text.secondary">
              ⏱️ Takes less than 2 minutes • No credit card required
            </Typography>
          </Stack>

          {/* Help section */}
          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Need help?&nbsp;
              <Box
                component="a"
                href="mailto:hello@terr.ai"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                contact support
              </Box>
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default NoPropertyLocation;
